import React, { createContext, useContext } from 'react';
import { useExternalLinkWarning } from '../hooks/useExternalLinkWarning';
import ExternalLinkWarningModal from '../components/common/ExternalLinkWarningModal';
import ExternalLinkErrorBoundary from '../components/common/ExternalLinkErrorBoundary';

// Create the context
const ExternalLinkWarningContext = createContext(null);

/**
 * Provider component for external link warning functionality
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const ExternalLinkWarningProvider = ({ children }) => {
  const {
    showWarning,
    targetUrl,
    isWarningDisabled,
    showExternalLinkWarning,
    handleContinue,
    handleCancel,
    resetPreferences
  } = useExternalLinkWarning();

  const contextValue = {
    // State
    showWarning,
    targetUrl,
    isWarningDisabled,
    
    // Methods
    showExternalLinkWarning,
    handleContinue,
    handleCancel,
    resetPreferences
  };

  return (
    <ExternalLinkWarningContext.Provider value={contextValue}>
      {children}
      
      {/* Global modal for external link warnings with error boundary */}
      <ExternalLinkErrorBoundary 
        targetUrl={targetUrl} 
        onClose={handleCancel}
      >
        <ExternalLinkWarningModal
          isOpen={showWarning}
          targetUrl={targetUrl}
          onContinue={handleContinue}
          onCancel={handleCancel}
        />
      </ExternalLinkErrorBoundary>
    </ExternalLinkWarningContext.Provider>
  );
};

/**
 * Hook to use the external link warning context
 * @returns {Object} Context value with warning state and methods
 * @throws {Error} If used outside of ExternalLinkWarningProvider
 */
export const useExternalLinkWarningContext = () => {
  const context = useContext(ExternalLinkWarningContext);
  
  if (!context) {
    throw new Error(
      'useExternalLinkWarningContext must be used within an ExternalLinkWarningProvider'
    );
  }
  
  return context;
};